import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PublicHeader, PublicFooter } from "@/components/layouts/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const businessCategories = [
  { key: "accounting_services", label: "Accounting Services" },
  { key: "advertising_marketing", label: "Advertising & Marketing" },
  { key: "agriculture_farming", label: "Agriculture & Farming" },
  { key: "architecture_design", label: "Architecture & Design" },
  { key: "auto_body_collision", label: "Auto Body & Collision" },
  { key: "auto_detailing", label: "Auto Detailing" },
  { key: "auto_repair_maintenance", label: "Auto Repair & Maintenance" },
  { key: "barber_shop_hair_salon", label: "Barber Shop / Hair Salon" },
  { key: "beauty_personal_care", label: "Beauty & Personal Care Services" },
  { key: "bookkeeping_services", label: "Bookkeeping Services" },
  { key: "brewery_taproom", label: "Brewery / Taproom" },
  { key: "cafe_coffee_shop", label: "Café / Coffee Shop" },
  { key: "catering_services", label: "Catering Services" },
  { key: "childcare_daycare", label: "Childcare & Daycare Services" },
  { key: "cleaning_services", label: "Cleaning Services (Residential & Commercial)" },
  { key: "construction_contracting", label: "Construction & Contracting" },
  { key: "consulting", label: "Consulting" },
  { key: "digital_media_content", label: "Digital Media & Content Creation" },
  { key: "education_training", label: "Education & Training" },
  { key: "electrical_services", label: "Electrical Services" },
  { key: "emotional_counseling_coaching", label: "Emotional Counseling & Coaching" },
  { key: "engineering", label: "Engineering" },
  { key: "fitness_studio_personal_training", label: "Fitness Studio / Personal Training" },
  { key: "food_truck_mobile_food", label: "Food Truck / Mobile Food Service" },
  { key: "gift_shop_boutique", label: "Gift Shop / Boutique" },
  { key: "government_public_sector", label: "Government & Public Sector (Local / Contracted)" },
  { key: "healthcare_medical_independent", label: "Healthcare & Medical (Independent Practice)" },
  { key: "home_care_services", label: "Home Care Services (Non-Medical)" },
  { key: "home_repair_handyman", label: "Home Repair / Handyman" },
  { key: "hospitality_tourism", label: "Hospitality & Tourism" },
  { key: "hvac_mechanical", label: "HVAC & Mechanical Services" },
  { key: "information_technology", label: "Information Technology Services" },
  { key: "insurance_independent_agent", label: "Insurance Services (Independent Agent)" },
  { key: "landscaping_lawn_care", label: "Landscaping & Lawn Care" },
  { key: "laundry_dry_cleaning", label: "Laundry / Dry Cleaning" },
  { key: "legal_services_small_practice", label: "Legal Services (Solo / Small Practice)" },
  { key: "logistics_delivery", label: "Logistics & Delivery Services" },
  { key: "manufacturing_small_batch", label: "Manufacturing (Small Batch / Local)" },
  { key: "nail_salon_spa", label: "Nail Salon & Spa" },
  { key: "nonprofit_social_services", label: "Nonprofit & Social Services" },
  { key: "painting_finishing", label: "Painting & Finishing Services" },
  { key: "pest_control", label: "Pest Control Services" },
  { key: "pet_services", label: "Pet Services (Grooming, Boarding, Training)" },
  { key: "plumbing_services", label: "Plumbing Services" },
  { key: "professional_services_general", label: "Professional Services (General)" },
  { key: "real_estate_independent", label: "Real Estate Services (Independent)" },
  { key: "restaurant_full_service", label: "Restaurant (Full Service)" },
  { key: "retail_clothing_apparel", label: "Retail – Clothing & Apparel" },
  { key: "retail_convenience_small_market", label: "Retail – Convenience / Small Market" },
  { key: "retail_furniture_home_goods", label: "Retail – Furniture & Home Goods" },
  { key: "retail_specialty_goods", label: "Retail – Specialty Goods" },
  { key: "self_publishing_authors", label: "Self-Publishing & Independent Authors" },
  { key: "software_saas_small_teams", label: "Software & SaaS (Small Teams)" },
  { key: "telecommunications", label: "Telecommunications Services" },
  { key: "transportation_freight_local", label: "Transportation & Freight (Local)" },
  { key: "wholesale_distribution_small", label: "Wholesale & Distribution (Small Scale)" },
  { key: "other", label: "Other (Please Specify)" }
];

const LeadCapturePage = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    business_name: "",
    business_category: "",
    business_category_other: "", // Free-text for "Other" selection
    first_name: "",
    last_name: "",
    telephone: "",
    email: "",
    email_contact_authorized: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email_contact_authorized) {
      toast.error("Please authorize email contact to proceed.");
      return;
    }

    setIsSubmitting(true);

    try {
      // Combine first_name and last_name into contact_name for backend compatibility
      const submitData = {
        ...formData,
        contact_name: `${formData.first_name} ${formData.last_name}`.trim()
      };
      const response = await axios.post(`${API}/public/lead-capture`, submitData);
      toast.success("Thank you! Let's learn more about your business.");
      // Redirect to questionnaire (Step 2)
      navigate(`/questionnaire/${response.data.id}`);
    } catch (error) {
      // Handle validation errors (Pydantic returns objects)
      let message = "Something went wrong. Please try again.";
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic validation error array
        message = detail.map(err => err.msg || err.message || String(err)).join(', ');
      } else if (detail?.msg) {
        message = detail.msg;
      }
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      <section className="py-24 bg-gradient-to-b from-navy-950 to-navy-900">
        <div className="max-w-xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-primary font-medium mb-4 tracking-wide uppercase text-sm">
              Step 1 of 2
            </p>
            <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight">
              Get Your Free A.I. Business Evaluation
            </h1>
            <p className="text-slate-300">
              Tell us about your business and we'll generate a personalized evaluation report at no cost.
            </p>
          </div>

          <div className="glass p-8 rounded-2xl" data-testid="lead-capture-form">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="business_name" className="text-slate-200">Business Name *</Label>
                <Input
                  id="business_name"
                  value={formData.business_name}
                  onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                  placeholder="Your Company Name"
                  required
                  className="bg-navy-900/50 border-slate-700 focus:border-primary text-white placeholder:text-slate-500 h-12"
                  data-testid="lead-business-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="business_category" className="text-slate-200">Business Category *</Label>
                <Select
                  value={formData.business_category}
                  onValueChange={(value) => setFormData({ 
                    ...formData, 
                    business_category: value,
                    business_category_other: value === "other" ? formData.business_category_other : ""
                  })}
                  required
                >
                  <SelectTrigger 
                    className="bg-navy-900/50 border-slate-700 focus:border-primary text-white h-12"
                    data-testid="lead-business-category"
                  >
                    <SelectValue placeholder="Select your industry" />
                  </SelectTrigger>
                  <SelectContent className="bg-navy-900 border-slate-700 max-h-[300px]">
                    {businessCategories.map((category) => (
                      <SelectItem
                        key={category.key}
                        value={category.key}
                        className="text-white hover:bg-primary/20 focus:bg-primary/20"
                      >
                        {category.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* "Other" free-text clarification field */}
              {formData.business_category === "other" && (
                <div className="space-y-2">
                  <Label htmlFor="business_category_other" className="text-slate-200">Please Specify Your Industry</Label>
                  <Input
                    id="business_category_other"
                    value={formData.business_category_other}
                    onChange={(e) => setFormData({ ...formData, business_category_other: e.target.value })}
                    placeholder="Describe your business type"
                    className="bg-navy-900/50 border-slate-700 focus:border-primary text-white placeholder:text-slate-500 h-12"
                    data-testid="lead-business-category-other"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="text-slate-200">First Name *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder="First Name"
                    required
                    className="bg-navy-900/50 border-slate-700 focus:border-primary text-white placeholder:text-slate-500 h-12"
                    data-testid="lead-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name" className="text-slate-200">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    placeholder="Last Name"
                    required
                    className="bg-navy-900/50 border-slate-700 focus:border-primary text-white placeholder:text-slate-500 h-12"
                    data-testid="lead-last-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="telephone" className="text-slate-200">Phone Number *</Label>
                <Input
                  id="telephone"
                  type="tel"
                  value={formData.telephone}
                  onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                  placeholder="+1 (555) 000-0000"
                  required
                  className="bg-navy-900/50 border-slate-700 focus:border-primary text-white placeholder:text-slate-500 h-12"
                  data-testid="lead-telephone"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-200">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@company.com"
                  required
                  className="bg-navy-900/50 border-slate-700 focus:border-primary text-white placeholder:text-slate-500 h-12"
                  data-testid="lead-email"
                />
              </div>

              <div className="flex items-start gap-3 p-4 rounded-lg bg-navy-950/50">
                <Checkbox
                  id="consent"
                  checked={formData.email_contact_authorized}
                  onCheckedChange={(checked) => 
                    setFormData({ ...formData, email_contact_authorized: checked })
                  }
                  className="mt-0.5 border-slate-600 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                  data-testid="lead-consent"
                />
                <Label htmlFor="consent" className="text-sm text-slate-300 font-normal leading-relaxed cursor-pointer">
                  I authorize A.I. Means Business to contact me via email regarding my advisory engagement. 
                  I understand that my information will be handled in accordance with the privacy policy. *
                </Label>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting || !formData.email_contact_authorized}
                className="w-full bg-primary hover:bg-primary/90 text-white font-bold rounded-full py-6 text-lg shadow-[0_0_20px_rgba(217,119,6,0.3)] disabled:opacity-50"
                data-testid="lead-submit"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    Continue to Questionnaire
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>

              <p className="text-xs text-slate-500 text-center">
                No credit card required. Your information is secure and will never be shared.
              </p>
            </form>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default LeadCapturePage;
